import { useCallback, useEffect, useRef, useState } from 'react';

const CASES = [
  {
    slug: 'mivi',
    img: '/images/mivi.webp',
    alt: 'MIVI',
    stats: [
      { num: '3', sup: 'x', foot: 'workflows in one app' },
      { num: '−40', sup: '%', foot: 'support tickets' },
    ],
    name: 'MIVI Team',
    role: 'Consumer Audio · India',
    quote:
      '"It streamlined invoice downloads, upsell flows, and order cancellations across our store — multiple workflows in one place."',
    brand: 'MIVI',
  },
  {
    slug: 'bricks-and-figs-de',
    img: '/images/bricksandfigsde.webp',
    alt: 'BricksAndFigsDE',
    stats: [
      { num: '−62', sup: '%', foot: 'manual order edits' },
      { num: '5', sup: 'min', foot: 'to go live' },
    ],
    name: 'The team at BricksAndFigsDE',
    role: 'Custom LEGO Star Wars Minifigures · Germany',
    quote:
      '"Self-serve edits took the manual order changes off our plate entirely. Our support tickets dropped almost overnight."',
    brand: 'BricksAndFigsDE',
  },
  {
    slug: 'fresh-cut-paper',
    img: '/images/freshcutpaper.webp',
    alt: 'FreshCut Paper',
    stats: [
      { num: '+14', sup: '%', foot: 'AOV lift' },
      { num: '30', sup: 'd', foot: 'to positive ROI' },
    ],
    name: 'FreshCut Paper Team',
    role: 'Pop-Up Paper Bouquets · Concord, MA',
    quote:
      '"Post-purchase upsells lifted our average order value within weeks — it\'s the easiest revenue we\'ve added all year."',
    brand: 'FreshCut Paper',
  },
  {
    slug: 'mysecretcase',
    img: '/images/mysecretcase.webp',
    alt: 'mysecretcase',
    stats: [
      { num: '38', sup: '%', foot: 'cancels saved' },
      { num: '$48', sup: 'K', foot: 'revenue recovered' },
    ],
    name: 'MySecretCase Team',
    role: "Italy's Leading Intimate-Wellness Retailer · Milan",
    quote:
      '"Cancel deflection recovered revenue we used to lose to refunds. Customers stay, and so does the order value."',
    brand: 'mysecretcase',
  },
];

export default function CaseStudies() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: 0 });

  const stepSize = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    const cards = track.querySelectorAll('.case');
    if (cards.length < 2) return track.clientWidth;
    return cards[1].offsetLeft - cards[0].offsetLeft;
  }, []);

  const onScroll = useCallback(() => {
    const s = stepSize();
    if (!s) return;
    const idx = Math.max(0, Math.min(CASES.length - 1, Math.round(trackRef.current.scrollLeft / s)));
    setActive(idx);
  }, [stepSize]);

  const goTo = (i) => {
    const track = trackRef.current;
    const cards = track?.querySelectorAll('.case');
    if (cards && cards[i]) track.scrollTo({ left: cards[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };
  const nudge = (dir) => trackRef.current?.scrollBy({ left: dir * stepSize(), behavior: 'smooth' });

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onDown = (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      drag.current = { down: true, startX: e.clientX, startScroll: track.scrollLeft, moved: 0 };
      track.classList.add('is-dragging');
      try { track.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onMove = (e) => {
      if (!drag.current.down) return;
      const dx = e.clientX - drag.current.startX;
      drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
      track.scrollLeft = drag.current.startScroll - dx;
    };
    const endDrag = (e) => {
      if (!drag.current.down) return;
      drag.current.down = false;
      track.classList.remove('is-dragging');
      try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      const s = stepSize();
      if (s) track.scrollTo({ left: Math.round(track.scrollLeft / s) * s, behavior: 'smooth' });
    };
    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    return () => {
      track.removeEventListener('pointerdown', onDown);
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', endDrag);
      track.removeEventListener('pointercancel', endDrag);
    };
  }, [stepSize]);

  const guardClick = (e) => {
    if (drag.current.moved > 6) e.preventDefault();
  };

  return (
    <section className="cases">
      <div className="cases__container">
        <div className="cases__head">
          <div className="cases__head-text">
            <span className="section-label">Case Studies</span>
            <h2 className="section-title">Customers, on the record.</h2>
            <p className="cases__sub">
              Real Shopify brands, real numbers — straight from the operators running the playbook.
            </p>
          </div>
          <a href="/case-studies" className="btn btn--primary cases__head-btn">
            View all case studies <i className="ph ph-arrow-right" aria-hidden="true" />
          </a>
        </div>

        <div className="cases__slider">
          <div className="cases__track" id="casesTrack" ref={trackRef} onScroll={onScroll}>
            {CASES.map((c) => (
              <a href={`/case-studies/${c.slug}`} className="case" key={c.brand} draggable="false" onClick={guardClick}>
                <img src={c.img} alt={c.alt} className="case__bg" draggable="false" />
                <div className="case__stats">
                  {c.stats.map((st, i) => (
                    <div className="case__stat-card" key={i}>
                      <div className="case__stat-num">
                        {st.num}
                        <span className="case__stat-sup">{st.sup}</span>
                      </div>
                      <div className="case__stat-foot">{st.foot}</div>
                    </div>
                  ))}
                </div>
                <div className="case__card">
                  <div className="case__top">
                    <i className="ph ph-quotes case__qicon" aria-hidden="true" />
                    <div className="case__person">
                      <span className="case__name">{c.name}</span>
                      <span className="case__role">{c.role}</span>
                    </div>
                  </div>
                  <p className="case__quote">{c.quote}</p>
                  <span className="case__brand">{c.brand}</span>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="cases__controls">
          <div className="cases__dots">
            {CASES.map((c, i) => (
              <button
                type="button"
                key={c.brand}
                className={`cases__dot${i === active ? ' is-active' : ''}`}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
          <div className="cases__arrows">
            <button className="cases__arrow" aria-label="Previous" onClick={() => nudge(-1)}>
              <i className="ph ph-arrow-left" aria-hidden="true" />
            </button>
            <button className="cases__arrow" aria-label="Next" onClick={() => nudge(1)}>
              <i className="ph ph-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
